import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { AlertTriangle, ArrowRight, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSyncChanges } from '@/api/queries/sync-queries'
import { SearchInput } from '@/components/commons/search-input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { cn } from '@/lib/utils'
import type {
	SyncChangeAction,
	SyncChangeEntry,
	SyncLogEntry,
} from '@/types/sync'

const PAGE_SIZE = 50
const ACTIONS: SyncChangeAction[] = [
	'CREATED',
	'UPDATED',
	'REMOVED',
	'RELINKED',
]

const ACTION_STYLES: Record<SyncChangeAction, string> = {
	CREATED: 'border-green-200 bg-green-50 text-green-700',
	UPDATED: 'border-blue-200 bg-blue-50 text-blue-700',
	REMOVED: 'border-amber-200 bg-amber-50 text-amber-700',
	RELINKED: 'border-purple-200 bg-purple-50 text-purple-700',
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T/

function formatValue(value: unknown): string {
	if (value === null || value === undefined || value === '') return '—'
	if (typeof value === 'string' && ISO_DATE.test(value)) {
		return format(new Date(value), 'dd/MM/yyyy HH:mm', { locale: ptBR })
	}
	return String(value)
}

interface SyncChangesDialogProps {
	log: SyncLogEntry | null
	onClose: () => void
}

export function SyncChangesDialog({ log, onClose }: SyncChangesDialogProps) {
	const { t } = useTranslation()
	const tp = 'administration.syncProtheus.changes'

	const [page, setPage] = useState(1)
	const [action, setAction] = useState<SyncChangeAction | ''>('')
	const [search, setSearch] = useState('')
	const { debouncedValue: key } = useDebouncedValue(search.trim(), 400)

	// Reinicia filtros ao abrir outro log e volta à 1ª página ao mudar um filtro
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset intencional quando o log muda
	useEffect(() => {
		setAction('')
		setSearch('')
	}, [log?.id])
	// biome-ignore lint/correctness/useExhaustiveDependencies: volta à 1ª página quando qualquer filtro muda
	useEffect(() => setPage(1), [log?.id, action, key])

	const changesQuery = useSyncChanges(log?.id ?? null, {
		page,
		limit: PAGE_SIZE,
		action: action || undefined,
		key: key || undefined,
	})
	const pagination = changesQuery.data?.pagination
	const items = changesQuery.data?.items ?? []

	return (
		<Dialog open={!!log} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="flex max-h-[90vh] flex-col gap-4 sm:max-w-5xl">
				<DialogHeader>
					<DialogTitle>{t(`${tp}.title`)}</DialogTitle>
					{log && (
						<DialogDescription>
							{t(`administration.syncProtheus.type_${log.syncType}`)} ·{' '}
							{format(new Date(log.createdAt), 'dd/MM/yyyy HH:mm', {
								locale: ptBR,
							})}{' '}
							· {log.user?.name ?? t('administration.syncProtheus.system')}
						</DialogDescription>
					)}
				</DialogHeader>

				{log?.status === 'FAILED' && log.error && (
					<div className="flex items-start gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
						<AlertTriangle className="mt-0.5 size-4 shrink-0" />
						<span className="break-all">{log.error}</span>
					</div>
				)}

				<div className="flex flex-col gap-2 sm:flex-row">
					<SearchInput
						value={search}
						onChange={setSearch}
						placeholder={t(`${tp}.searchPlaceholder`)}
						className="sm:flex-1"
					/>
					<div className="flex gap-2">
						<NativeSelect
							value={action}
							onChange={(e) =>
								setAction(e.target.value as SyncChangeAction | '')
							}
							aria-label={t(`${tp}.actionFilter`)}
						>
							<NativeSelectOption value="">
								{t(`${tp}.allActions`)}
							</NativeSelectOption>
							{ACTIONS.map((a) => (
								<NativeSelectOption key={a} value={a}>
									{t(`${tp}.action_${a}`)}
								</NativeSelectOption>
							))}
						</NativeSelect>
					</div>
				</div>

				<div className="min-h-0 flex-1 overflow-auto rounded-xl border">
					{changesQuery.isLoading ? (
						<div className="flex items-center justify-center py-10 text-muted-foreground">
							<Loader2 className="mr-2 size-5 animate-spin" />
							{t(`${tp}.loading`)}
						</div>
					) : changesQuery.isError ? (
						<div className="flex items-center gap-2 px-4 py-3 text-sm text-red-700">
							<AlertTriangle className="size-4 shrink-0" />
							{t(`${tp}.error`)}
						</div>
					) : items.length === 0 ? (
						<p className="py-8 text-center text-sm text-muted-foreground">
							{log?.status === 'RUNNING'
								? t(`${tp}.running`)
								: t(`${tp}.empty`)}
						</p>
					) : (
						<SyncChangesTable items={items} tp={tp} />
					)}
				</div>

				{pagination && pagination.total > 0 && (
					<div className="flex items-center justify-between text-xs text-muted-foreground">
						<span>
							{t(`${tp}.pageInfo`, {
								total: pagination.total,
								page: pagination.page,
								totalPages: pagination.totalPages,
							})}
						</span>
						<div className="flex gap-2">
							<Button
								variant="outline"
								size="sm"
								disabled={page <= 1 || changesQuery.isFetching}
								onClick={() => setPage((p) => p - 1)}
							>
								{t(`${tp}.previous`)}
							</Button>
							<Button
								variant="outline"
								size="sm"
								disabled={
									page >= pagination.totalPages || changesQuery.isFetching
								}
								onClick={() => setPage((p) => p + 1)}
							>
								{t(`${tp}.next`)}
							</Button>
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	)
}

function SyncChangesTable({
	items,
	tp,
}: {
	items: SyncChangeEntry[]
	tp: string
}) {
	const { t } = useTranslation()

	return (
		<Table>
			<TableHeader>
				<TableRow className="bg-muted/50">
					{[
						t(`${tp}.colAction`),
						t(`${tp}.colEntity`),
						t(`${tp}.colKey`),
						t(`${tp}.colDiff`),
					].map((h) => (
						<TableHead
							key={h}
							className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"
						>
							{h}
						</TableHead>
					))}
				</TableRow>
			</TableHeader>
			<TableBody>
				{items.map((change) => (
					<TableRow key={change.id} className="align-top">
						<TableCell className="px-3 py-2">
							<Badge
								variant="outline"
								className={cn(ACTION_STYLES[change.action])}
							>
								{t(`${tp}.action_${change.action}`)}
							</Badge>
						</TableCell>
						<TableCell className="px-3 py-2 text-xs">
							{t(`${tp}.entity_${change.entityType}`)}
						</TableCell>
						<TableCell className="px-3 py-2 font-mono text-xs">
							{change.key}
						</TableCell>
						<TableCell className="px-3 py-2">
							<DiffList diff={change.diff} action={change.action} tp={tp} />
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	)
}

function DiffList({
	diff,
	action,
	tp,
}: {
	diff: SyncChangeEntry['diff']
	action: SyncChangeAction
	tp: string
}) {
	const { t } = useTranslation()
	if (!diff) return <span className="text-xs text-muted-foreground">—</span>

	return (
		<ul className="flex flex-col gap-1">
			{Object.entries(diff).map(([field, { from, to }]) => (
				<li key={field} className="flex flex-wrap items-center gap-1 text-xs">
					<span className="font-medium text-kaefer-dark-grey">
						{t(`${tp}.field_${field}`, { defaultValue: field })}:
					</span>
					{action === 'REMOVED' ? (
						<span className="break-all">{formatValue(from)}</span>
					) : (
						<>
							{from !== null && (
								<>
									<span className="break-all text-muted-foreground line-through decoration-muted-foreground/40">
										{formatValue(from)}
									</span>
									<ArrowRight className="size-3 shrink-0 text-muted-foreground" />
								</>
							)}
							<span className="break-all">{formatValue(to)}</span>
						</>
					)}
				</li>
			))}
		</ul>
	)
}
